import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { Breadcrumb } from '~/components/Breadcrumps';
import { Button } from '~/components/Button';
import { DetailsView } from '~/components/DetailsView';
import { useConfirm, useMessage } from '~/components/Dialogs';
import { onSubmitError } from '~/components/Form';
import { useModals } from '~/components/Modal';
import { Progress } from '~/components/Progress';
import { BreadcrumbsResourcePage, ResourceBlock } from '~/components/ResourcePage';
import { Table } from '~/components/Table';
import { Title } from '~/components/Title';
import { TagsList } from '~/components/Controls/Tags';
import { LinksList } from '~/components/Controls/LinksList';
import { EditorInput } from '~/components/Controls/Editor';
import { CopyableText } from '~/components/CopyableText';
import { useAbility } from '~/lib/ability';
import { renderBoolean } from '~/lib/helpers';
import { Intent } from '~/lib/intent';
import { ResourceLoader, useResource } from '~/lib/loader';
import { Pagination } from '~/components/Pagination';
import { clientsLinks } from '~/modules/Clients';
import { fetchAgencies } from '~/modules/Clients/api';
import { IAgencyShort } from '~/modules/Clients/models/Agency';
import { SourcesAbility } from '~/modules/Sources/ability';
import {
    fetchSource,
    prepareSourceSubmitData,
    toggleSource,
    updateSource,
} from '~/modules/Sources/api';
import { usePlacements } from '~/modules/Sources/usePlacements';
import { PlacementsSearch } from '~/modules/Sources/components/PlacementsSearch';
import { SourceForm } from '~/modules/Sources/forms/SourceForm';
import {
    PriceRounding,
    roundingAdTypes,
    RoundingAdType,
} from '~/modules/Sources/models/Source';
import { IFeeOverrideSetting } from '~/modules/Sources/models/FeeOverrideSetting';
import { ISourceDomains } from '~/modules/Sources/models/IDomains';
import { sourcesLinks } from '~/modules/Sources/links';
import style from '~/modules/Sources/index.module.scss';

interface ISourceViewParams {
    sourceId: string;
}

const formatList = (values?: (string | number)[] | null) =>
    values && values.length ? values.join(', ') : '—';

const formatConfiguration = (configuration: string | null) => {
    if (!configuration) {
        return '';
    }
    try {
        return JSON.stringify(JSON.parse(configuration), null, 2);
    } catch (error) {
        return configuration;
    }
};

const formatPriceRounding = (priceRounding: PriceRounding | undefined, type: RoundingAdType) => {
    if (!priceRounding) {
        return null;
    }
    const value = priceRounding[type];
    if (value == null) {
        return null;
    }
    if (Array.isArray(value)) {
        return value.join('\n');
    }
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
};

export const SourceView = () => {
    const params = useParams<ISourceViewParams>();
    const ability = useAbility(SourcesAbility);
    const confirm = useConfirm();
    const message = useMessage();
    const modals = useModals();

    const sourceResult = useResource(() => fetchSource(params.sourceId));
    const agenciesResult = useResource(() => fetchAgencies(), []);
    const placements = usePlacements(params.sourceId);

    const agenciesById = useMemo(() => {
        const map = new Map<number, IAgencyShort>();
        (agenciesResult.resource || []).forEach((agency) => {
            map.set(agency.id, agency);
        });
        return map;
    }, [agenciesResult.resource]);

    const editSourceModal = modals.create(SourceForm, {
        lockSeller: false,
        onSubmit: (source, formikActions, close) => {
            updateSource(prepareSourceSubmitData(source))
                .then((updatedSource) => {
                    sourceResult.setResource(updatedSource);
                    placements.reload();
                    close();
                })
                .catch(onSubmitError(formikActions));
        },
    });

    const renderDomains = (domains: ISourceDomains[] = []) => (
        <Table
            header
            records={domains}
            columns={[
                { header: 'Domain', field: 'domain' },
                { header: 'Bundles', cell: (domain) => domain.bundles || '—' },
                { header: 'Custom ID', cell: (domain) => domain.customId || '—' },
                { header: 'Default', cell: (domain) => renderBoolean(domain.isDefault) },
            ]}
        />
    );

    const renderFeeOverrides = (overrides: IFeeOverrideSetting[] = []) => (
        <Table
            header
            records={overrides}
            columns={[
                { header: 'Ad type', cell: (override) => override.adType || 'Any' },
                { header: 'Country', cell: (override) => override.country || 'Any' },
                { header: 'Scale', cell: (override) => override.scale ?? '—' },
                { header: 'Fee', cell: (override) => override.fee ?? '—' },
            ]}
        />
    );

    const renderPlacements = () => (
        <>
            <PlacementsSearch placements={placements} className={style.placementsSearch} />
            <Table
                header
                records={placements.items}
                placeholder={placements.loading && <Progress message="Loading placements" />}
                columns={[
                    { header: 'Name', field: 'name' },
                    { header: 'ID', cellWrap: 'break-word', headerAttrs: { className: style.idCell }, cellAttrs: { className: style.idCell }, cell: (p) => <CopyableText>{p.id || ''}</CopyableText> },
                    { header: 'Platform', field: 'platform' },
                    { header: 'Bundle', field: 'bundle' },
                    { header: 'Ad type', field: 'adType' },
                    { header: 'Placement Type', field: 'placementType' },
                    { header: 'HVA', cell: (p) => renderBoolean(p.hva) },
                    { header: 'Pricefloor', field: 'pricefloor' },
                    { header: 'MAX Ad Unit ID', field: 'maxAdUnitId', cellWrap: 'break-word' },
                    { header: 'GPID', field: 'gpid', cellWrap: 'break-word' },
                ]}
            />
            <Pagination
                padding={4}
                limit={placements.pageSize}
                offset={placements.pageNo * placements.pageSize}
                total={placements.count}
                onChange={placements.changePage}
                limits={[25, 50, 100, 200]}
            />
        </>
    );

    const handleToggle = (source) =>
        confirm(`${source.active ? 'Deactivate' : 'Activate'} this source?`, {
            icon: <i className="fa fa-question" />,
            intent: source.active ? Intent.WARNING : Intent.SUCCESS,
            onOk: () =>
                toggleSource(source, !source.active)
                    .then((updated) => {
                        sourceResult.setResource(updated);
                    })
                    .catch(() =>
                        message(`Can't ${source.active ? 'deactivate' : 'activate'} source`, {
                            icon: <i className="fa fa-times" />,
                            intent: Intent.DANGER,
                        }),
                    ),
        });

    const renderOverviewFields = (source) => [
        { label: 'ID', value: source.id },
        {
            label: 'Name',
            value: source.name,
        },
        {
            label: 'Seller',
            value: (
                <NavLink to={clientsLinks.seller(source.sellerId)}>
                    #{source.sellerId}
                </NavLink>
            ),
        },
        {
            label: 'Active',
            value: renderBoolean(source.active),
        },
        {
            label: 'Category',
            value: source.category || '—',
        },
        {
            label: 'Traffic source',
            value: source.trafficSource || '—',
        },
        {
            label: 'Charge schema',
            value: source.chargeSchema || '—',
        },
        {
            label: 'KS',
            value: source.ks || '—',
        },
        {
            label: 'Integration source ID',
            value: source.integrationSourceId || '—',
        },
        {
            label: 'Primary domain',
            value: source.domain || '—',
        },
        {
            label: 'Tags',
            value: source.tags?.length ? <TagsList values={source.tags} size="small" /> : '—',
        },
    ];

    const renderLimitsFields = (source) => [
        {
            label: 'Fee',
            value: source.fee ?? '—',
        },
        {
            label: 'Tmax',
            value: source.tmax ?? '—',
        },
        {
            label: 'Scale',
            value: source.scale ?? '—',
        },
        {
            label: 'Max RPS',
            value: source.maxRps ?? '—',
        },
    ];

    const renderRestrictionsFields = (source) => [
        {
            label: 'BCAT',
            value: formatList(source.bcat),
        },
        {
            label: 'BAPP',
            value: formatList(source.bapp),
        },
        {
            label: 'BADV',
            value: formatList(source.badv),
        },
    ];

    const renderAgencyLinks = (agencies: number[] = []) => {
        if (!agencies || agencies.length === 0) {
            return '—';
        }

        const items = agencies.map((agencyId) => ({
            id: agencyId,
            label: `${agenciesById.get(agencyId)?.title || `Agency #${agencyId}`} (#${agencyId})`,
        }));

        return (
            <LinksList
                values={items.map((item) => item.label)}
                maxItems={12}
                buildTo={(label) => {
                    const matched = items.find((item) => item.label === label);
                    const targetId = matched?.id ?? items[0].id;
                    return clientsLinks.agency(targetId);
                }}
            />
        );
    };

    const renderFieldRows = (fields) => (
        <div className={style.infoList}>
            {fields.map((field) => (
                <div className={style.infoItem} key={field.label}>
                    <div className={style.infoLabel}>{field.label}</div>
                    <div className={style.infoValue}>{field.value ?? '—'}</div>
                </div>
            ))}
        </div>
    );

    const renderAgenciesFields = (source) => [
        {
            label: 'No charge agencies',
            value: renderAgencyLinks(source.nochargeAgencies),
        },
        {
            label: 'No import agencies',
            value: renderAgencyLinks(source.noimportAgencies),
        },
        {
            label: 'Direct relation agencies',
            value: renderAgencyLinks(source.directRelationAgencies),
        },
    ];


    return (
        <ResourceLoader result={sourceResult} message="Loading source">
            {({ resource: source, fetch }) =>
                source && (
                    <Breadcrumb breadcrumb={{ to: sourcesLinks.sources(), title: 'Sources' }}>
                        <Breadcrumb breadcrumb={{ to: sourcesLinks.source(source.id), title: source.name }}>
                            <BreadcrumbsResourcePage
                                title={`${source.name}`}
                                toolbar={
                                    <>
                                        <Button
                                            key="reload"
                                            responsive
                                            leftIcon={<i className="fa fa-refresh" />}
                                            onClick={() => {
                                                fetch();
                                                placements.reload();
                                            }}
                                        >
                                            Reload
                                        </Button>
                                        {ability.canEdit() && (
                                                <Button
                                                    key="toggle"
                                                    responsive
                                                intent={source.active ? Intent.WARNING : Intent.DEFAULT}
                                                    leftIcon={<i className="fa fa-power-off" />}
                                                    onClick={() => handleToggle(source)}
                                                >
                                                    {source.active ? 'Deactivate' : 'Activate'}
                                                </Button>
                                        )}
                                        {ability.canEdit() && (
                                            <Button
                                                key="edit"
                                                responsive
                                                intent={Intent.PRIMARY}
                                                leftIcon={<i className="fa fa-edit" />}
                                                onClick={() =>
                                                    editSourceModal.show(
                                                        { source },
                                                        { size: "wide" }
                                                    )
                                                }
                                            >
                                                Edit
                                            </Button>
                                        )}
                                    </>
                                }
                            >
                                <>
                                    <DetailsView fields={renderOverviewFields(source)} />
                                    <div className={style.summaryGrid}>
                                        <ResourceBlock title="Limits & Billing" className={style.summaryCard}>
                                            {renderFieldRows(renderLimitsFields(source))}
                                        </ResourceBlock>
                                        <ResourceBlock title="Restrictions" className={style.summaryCard}>
                                            {renderFieldRows(renderRestrictionsFields(source))}
                                        </ResourceBlock>
                                        <ResourceBlock title="Price rounding" className={style.summaryCard}>
                                            <div className={style.priceRounding}>
                                                {roundingAdTypes.map(({ value, label }) => {
                                                    const formatted = formatPriceRounding(source.priceRounding, value);
                                                    return (
                                                        <div className={style.priceRoundingRow} key={value}>
                                                            <div className={style.priceRoundingLabel}>{label}</div>
                                                            {formatted ? (
                                                                <EditorInput
                                                                    mode="text"
                                                                    minLines={3}
                                                                    maxLines={8}
                                                                    disabled
                                                                    value={formatted}
                                                                    id={`price-rounding-${value}`}
                                                                />
                                                            ) : (
                                                                <span className={style.empty}>—</span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </ResourceBlock>
                                    </div>
                                    <div className="layout-content-box">
                                        <Title>Domains</Title>
                                        {renderDomains(source.domains)}
                                    </div>
                                    <div className="layout-content-box">
                                        <Title>Fee overrides</Title>
                                        {renderFeeOverrides(source.feeOverrideConfiguration)}
                                    </div>
                                    <div className="layout-content-box">
                                        <Title>Placements</Title>
                                        {renderPlacements()}
                                    </div>
                                    <div className="layout-content-box">
                                        <Title>Configuration</Title>
                                        <EditorInput
                                            mode="json"
                                            minLines={4}
                                            maxLines={20}
                                            disabled
                                            value={formatConfiguration(source.configuration)}
                                            id="source-config-preview"
                                        />
                                    </div>
                                </>
                            </BreadcrumbsResourcePage>
                        </Breadcrumb>
                    </Breadcrumb>
                )
            }
        </ResourceLoader>
    );
};
